import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { WorkspaceKind } from '../../types/workspace';

/**
 * Canonical workspace addresses.
 *
 * /dashboard/<kind> is the stable, shareable name for each product experience.
 * It deliberately does NOT render a second copy of any console — duplicating
 * six dashboards to satisfy a URL would be the exact mistake the architecture
 * exists to prevent. Instead it asks the authorization layer whether this
 * identity may enter that workspace, and hands over to the one implementation
 * that already exists.
 *
 * A refusal goes to /no-access with the reason, never to a blank screen and
 * never silently to somewhere the person did not ask for.
 */

const KINDS: Record<string, WorkspaceKind> = {
  student: 'student',
  campus: 'campus',
  provider: 'provider',
  opportunities: 'opportunity',
  opportunity: 'opportunity',
  community: 'community',
  housing: 'housing'
};
export function DashboardRoute() {
  const {
    kind
  } = useParams();
  const {
    authenticated
  } = useAuth();
  const {
    workspaces,
    enterWorkspace
  } = useWorkspace();
  if (!authenticated) return <Navigate to="/signin" replace data-id="element-8872" data-mp="k47h5jm6r5xi" />;
  const resolved = kind ? KINDS[kind] : undefined;
  if (!resolved) return <Navigate to="/dashboard" replace data-id="element-8873" data-mp="p1g1nqut3m1x" />;

  /* The client asked for a workspace. That is a claim, not evidence — the
     membership register decides. */
  const match = workspaces.find(space => space.kind === resolved);
  if (!match) return <Navigate to={`/no-access?need=workspace.${resolved}`} replace data-id="element-8874" data-mp="164ydmk8e585" />;
  const decision = enterWorkspace(match.id);
  if (!decision.allowed) return <Navigate to={`/no-access?need=workspace.${resolved}`} replace data-id="element-8875" data-mp="658315wau9fl" />;
  return <Navigate to={match.path} replace data-id="element-8876" data-mp="1vko0vp4rw8q6" />;
}

/**
 * /dashboard with no kind: send people where their memberships actually lead.
 * One workspace opens directly; several is a choice, not a guess.
 */
export function DashboardIndex() {
  const {
    authenticated
  } = useAuth();
  const {
    workspaces
  } = useWorkspace();
  if (!authenticated) return <Navigate to="/signin" replace data-id="element-8877" data-mp="1u15w3at0qxjw" />;
  if (workspaces.length === 1) return <Navigate to={workspaces[0].path} replace data-id="element-8878" data-mp="1p4fjixgfak38" />;
  return <Navigate to="/workspaces" replace data-id="element-8879" data-mp="1k49lmzjk81cp" />;
}